import React from 'react';
import { Rnd } from 'react-rnd';
import { getTimelineBlockLayout } from './timelineMath';

const LANE_HEIGHT = 68;

const STATUS_STYLE = {
  draft: 'bg-slate-700/95 ring-slate-300/40',
  scheduled: 'bg-emerald-600/95 ring-emerald-100/50',
  live: 'bg-cyan-600/95 ring-cyan-100/50',
  paused: 'bg-amber-600/95 ring-amber-100/50',
  completed: 'bg-sky-700/95 ring-sky-100/40',
  cancelled: 'bg-rose-700/85 ring-rose-100/40',
};

export function getProgrammePublishHealth(timelineItems = []) {
  return {
    total: timelineItems.length,
    published: timelineItems.filter((item) => item.publishState === 'published').length,
    detached: timelineItems.filter((item) => item.managementState === 'detached').length,
  };
}

function ProgrammeBlock({ occurrence, rangeStart, rangeEnd, laneWidth, visibleWindows, interactive, onOpen, onMove }) {
  const layout = getTimelineBlockLayout({
    startsAt: occurrence.startsAtUtc,
    endsAt: occurrence.endsAtUtc,
    rangeStart,
    rangeEnd,
    laneWidth,
    visibleWindows,
  });
  if (!layout) return null;
  const movable = interactive && occurrence.sourceType === 'campaign'
    && !['cancelled', 'completed'].includes(occurrence.status);
  const durationMs = Date.parse(occurrence.endsAtUtc) - Date.parse(occurrence.startsAtUtc);
  const msPerPx = (rangeEnd.getTime() - rangeStart.getTime()) / laneWidth;
  const timelineItems = occurrence.timelineItems || [];
  const publishHealth = getProgrammePublishHealth(timelineItems);

  return (
    <Rnd
      bounds="parent"
      size={{ width: layout.width, height: 54 }}
      position={{ x: layout.left, y: 12 }}
      enableResizing={movable ? { left: true, right: true } : false}
      disableDragging={!movable}
      dragAxis="x"
      onDragStop={(_event, position) => {
        const startsAt = new Date(rangeStart.getTime() + position.x * msPerPx);
        onMove?.(occurrence, startsAt, new Date(startsAt.getTime() + durationMs));
      }}
      onResizeStop={(_event, _direction, ref, _delta, position) => {
        const startsAt = new Date(rangeStart.getTime() + position.x * msPerPx);
        const endsAt = new Date(startsAt.getTime() + ref.offsetWidth * msPerPx);
        onMove?.(occurrence, startsAt, endsAt);
      }}
      className={`overflow-hidden rounded-xl text-white ring-1 shadow-lg ${STATUS_STYLE[occurrence.status] || STATUS_STYLE.draft}`}
      style={{ zIndex: 16 }}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.(occurrence);
      }}
    >
      <button type="button" className="h-full w-full px-3 text-left">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[12px] font-semibold">{occurrence.title}</span>
          <span className="rounded-full bg-black/20 px-2 py-0.5 text-[9px] uppercase">{occurrence.programmeType}</span>
        </div>
        <div className="mt-1 truncate text-[10px] text-white/80">
          {occurrence.status} · {occurrence.runtimeStatus}
          {publishHealth.total ? ` · ${publishHealth.published}/${publishHealth.total} published` : ' · not generated'}
          {publishHealth.detached ? ` · ${publishHealth.detached} detached` : ''}
        </div>
      </button>
    </Rnd>
  );
}

export default function GigsCampaignsLane({
  occurrences,
  rangeStart,
  rangeEnd,
  laneWidth,
  visibleWindows,
  nowLine,
  interactive,
  onOpen,
  onMove,
}) {
  const msPerPx = (rangeEnd.getTime() - rangeStart.getTime()) / laneWidth;
  const phaseMarkers = (occurrences || []).flatMap((occurrence) => (
    (occurrence.timelineItems || []).map((item) => ({ occurrence, item }))
  )).filter(({ item }) => item.publishState !== 'suppressed');

  return (
    <div
      className="relative border-b border-indigo-300/20 bg-gradient-to-r from-indigo-500/10 via-sky-500/5 to-transparent"
      style={{ height: LANE_HEIGHT }}
    >
      {nowLine?.()}
      {phaseMarkers.map(({ occurrence, item }) => {
        const position = (Date.parse(item.scheduledForUtc) - rangeStart.getTime()) / msPerPx;
        if (position < 0 || position > laneWidth) return null;
        return (
          <button
            key={`${occurrence.id}:${item.id}`}
            type="button"
            className={`absolute top-1 h-4 w-1.5 rounded-full ${item.managementState === 'detached' ? 'bg-amber-300' : item.publishState === 'published' ? 'bg-cyan-300' : 'bg-slate-400'}`}
            style={{ left: position, zIndex: 18 }}
            title={`${occurrence.title}: ${item.title}`}
            onClick={() => onOpen?.(occurrence)}
          />
        );
      })}
      {(occurrences || []).map((occurrence) => (
        <ProgrammeBlock
          key={occurrence.id || occurrence.occurrenceKey}
          occurrence={occurrence}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          laneWidth={laneWidth}
          visibleWindows={visibleWindows}
          interactive={interactive}
          onOpen={onOpen}
          onMove={onMove}
        />
      ))}
      {!occurrences?.length ? (
        <div className="absolute inset-0 flex items-center px-3 text-[11px] uppercase tracking-[0.2em] text-slate-500">
          No gigs or campaigns in this visible range
        </div>
      ) : null}
    </div>
  );
}

export { LANE_HEIGHT };
